import brandColors from "app/styles"
import classnames from "classnames"
import { FC, useRef } from "react"
import Select, { Props as SelectProps, StylesConfig } from "react-select"
import CreatableSelect from "react-select/creatable"

type TOption = {
  label: string | Element
  value: string
  isDisabled?: boolean
}

type TIsMulti = true | false

// Props Styling is really weird.  We can't use tailwind. https://react-select.com/styles
export const DefaultSelectStyles: StylesConfig<TOption, TIsMulti> = {
  multiValue: () => {
    return {
      backgroundColor: "rgba(255,255,255,0.05)",
      borderRadius: "0.4rem",
      boxSizing: "border-box",
      display: "flex",
      label: "multiValue",
      fontSize: "14px",
      marginRight: "0.25rem",
      border: "1px solid rgba(255,255,255,0.07)",
    }
  },
  multiValueLabel: () => {
    return {
      boxSizing: "border-box",
      fontSize: "100%",
      overflow: "hidden",
      paddingLeft: 10,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      color: "#75FFF1",
      alignItems: "center",
      justifyContent: "center",
      display: "flex",
    }
  },
  multiValueRemove: () => {
    return {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "rgba(255,255,255,0.05)",
      borderTopRightRadius: "0.4rem",
      borderBottomRightRadius: "0.4rem",
      cursor: "pointer",
      marginLeft: 10,
      paddingLeft: 5,
      paddingRight: 5,
    }
  },
  dropdownIndicator: () => {
    return {
      opacity: 0.2,
      padding: "6px",
      transform: "scale(0.8)",
    }
  },
  menu: () => {
    return {
      position: "absolute",
      top: "100%",
      zIndex: "20",
      overflow: "hidden",
      boxSizing: "border-box",
      backgroundColor: brandColors.grayscale_dark,
      paddingTop: "0px !imporant",
      margin: "5px 0",
      width: "auto",
      minWidth: "200px",
      maxWidth: "600px",
      cursor: "pointer",
      borderRadius: "0.375rem",
      borderStyle: "solid",
      borderWidth: "1px",
      borderColor: "rgba(255, 255, 255, 0.07)",
      boxShadow: "0 0px 6px 0 rgba(0,0,0,0.25)",
    }
  },
  option: (provided, { isFocused, isSelected, isDisabled }) => {
    return {
      ...provided,
      cursor: "pointer",
      backgroundColor: isSelected
        ? brandColors.grayscale_light_50
        : isFocused
        ? brandColors.grayscale_light_25
        : brandColors.grayscale_dark,
      color: isDisabled ? "#686a6c" : "white",

      padding: "10px !important",
      borderTop: "0.5px solid rgba(255, 255, 255, 0.07)",
      ":first-of-type": {
        borderTop: "0px solid rgba(255, 255, 255, 0)",
      },
    }
  },
  placeholder: (provided) => {
    return {
      ...provided,
      color: "#6B7280",
    }
  },
  input: () => {
    return {
      ":focus-within": { outline: "none", border: "none" },
      margin: 2,
      outline: "none",
      border: "none",
      ":focus": { outline: "none", border: "none" },
      appearance: "none",
      boxShadow: "none",
      paddingBottom: 2,
      paddingTop: 2,
    }
  },
  indicatorSeparator: () => {
    return {
      display: "none",
    }
  },
  control: (provided, { selectProps: { width } }) => {
    return {
      alignItems: "center",
      borderRadius: "0.375rem",
      borderStyle: "solid",
      borderWidth: "1px",
      borderColor: "rgba(255, 255, 255, 0.1)",
      boxShadow: undefined,
      boxSizing: "border-box",
      cursor: "pointer",
      display: "flex",
      backgroundColor: "rgba(151 151 151,0.10)",
      ":focus-within": { borderColor: "rgba(255, 255, 255, 0.25)" },
      // flexWrap: "wrap",
      justifyContent: "space-between",
      label: "control",
      // minHeight: "42px",
      outline: "0 !important",
      position: "relative",
      transition: "all 100ms",
      width: width,
    }
  },
}

export const menuTopStyles = {
  menu: () => {
    return {
      position: "absolute",
      bottom: "100%",
      zIndex: "20",
      overflow: "hidden",
      boxSizing: "border-box",
      backgroundColor: brandColors.grayscale_dark,
      paddingTop: "0px !imporant",
      margin: "5px 0",
      width: "100%",
      minWidth: "200px",
      maxWidth: "400px",
      cursor: "pointer",
      borderRadius: "0.375rem",
      borderStyle: "solid",
      borderWidth: "1px",
      borderColor: "rgba(255, 255, 255, 0.07)",
      boxShadow: "0 0px 6px 0 rgba(0,0,0,0.25)",
    }
  },
}

export const borderlessStyles = {
  control: () => {
    return {
      alignItems: "center",
      boxSizing: "border-box",
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "space-between",
      flex: "0 1 auto !important",
      label: "control",
      minHeight: 10,
      outline: "0 !important",
      position: "relative",
      transition: "all 100ms",
      width: "auto",
    }
  },
}

const smallStyles = {
  control: (provided, { selectProps: { width } }) => {
    return {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      position: "relative",
      transition: "all 100ms",
      cursor: "pointer",
      label: "control",
      outline: "0 !important",
      borderRadius: "0.375rem",
      borderStyle: "solid",
      borderWidth: "1px",
      borderColor: "rgba(255, 255, 255, 0.07)",
      boxShadow: undefined,
      boxSizing: "border-box",
      minHeight: "10px",
      width: width,
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
    }
  },
  input: () => {
    return {
      margin: "3px",
      paddingTop: "0",
      paddingBottom: "0",
    }
  },
  dropdownIndicator: () => {
    return {
      padding: "4px",
    }
  },
  menuList: () => {
    return {
      fontSize: "0.875rem",
      lineHeight: "1.25rem",
    }
  },
}

const extraSmallStyles = {
  control: (provided, { selectProps: { width } }) => {
    return {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      position: "relative",
      transition: "all 100ms",
      cursor: "pointer",
      label: "control",
      outline: "0 !important",
      borderRadius: "0.375rem",
      borderStyle: "solid",
      borderWidth: "1px",
      borderColor: "rgba(255, 255, 255, 0.07)",
      boxShadow: undefined,
      boxSizing: "border-box",
      minHeight: "10px",
      width: width,
      fontSize: "0.75rem",
      lineHeight: "1rem",
    }
  },
  input: () => {
    return {
      margin: "3px",
      paddingTop: "0",
      paddingBottom: "0",
    }
  },
  dropdownIndicator: () => {
    return {
      padding: "4px",
    }
  },
  menuList: () => {
    return {
      fontSize: "0.75rem",
      lineHeight: "1rem",
    }
  },
}

export const DropdownSelect: FC<
  SelectProps & {
    creatable?: boolean
    overrideWrapperClassname?: string
    borderless?: boolean
    autoWidth?: boolean
    portalDropdown?: boolean
    portalAlignment?: "left" | "right"
    size?: "xs" | "sm" | "md"
    hideDropdownIndicator?: boolean
  }
> = ({ ...allProps }: SelectProps) => {
  const {
    creatable,
    styles: stylesFromProps,
    overrideWrapperClassname,
    selectClassName,
    portalDropdown,
    portalAlignment,
    borderless,
    autoWidth,
    size = "md",
    hideDropdownIndicator = false,
    components,
    ...props
  } = allProps
  const { isDisabled } = props

  const styles = {
    ...(borderless ? borderlessStyles : {}),
    ...(size === "sm" ? smallStyles : size === "xs" ? extraSmallStyles : {}),
    ...stylesFromProps,
    ...(allProps.menuPlacement === "bottom" ? menuTopStyles : {}),
  }

  const hiddenDropdownIndicator = {
    DropdownIndicator: () => null,
    IndicatorSeparator: () => null,
  }

  const ref = useRef<HTMLDivElement | null>(null)

  const selectProps = {
    styles: {
      ...DefaultSelectStyles,
      singleValue: (provided, state) => {
        return {
          ...provided,
          ...(autoWidth
            ? {
                // maxWidth: "none",
                position: "static",
                top: "auto",
                transform: "none",
                whitespace: "nowrap",
              }
            : {}),
          color: isDisabled ? "#979797" : "#FFFFFF",
        }
      },
      valueContainer: (provided, state) => {
        return {
          ...provided,
          ...(autoWidth
            ? { whitespace: "nowrap", display: "flex", flexDirection: "row", flexWrap: "none" }
            : {}),
        }
      },
      ...styles,
      ...(portalDropdown
        ? {
            menu: () => {
              return {
                position: "fixed",
                // transform: `translateY(-${window?.scrollY || 0}px)`,
                top: ref.current
                  ? `${
                      ref.current.getBoundingClientRect().y +
                      ref.current.getBoundingClientRect().height
                    }px`
                  : "0px",

                [portalAlignment]: ref.current
                  ? `${ref.current.getBoundingClientRect()[portalAlignment]}px`
                  : "0px",
                zIndex: "9999",
                overflow: "hidden",
                boxSizing: "border-box",
                backgroundColor: brandColors.grayscale_dark,
                paddingTop: "0px !imporant",
                margin: "5px 0",
                width: "auto",
                minWidth: "200px",
                maxWidth: "600px",
                cursor: "pointer",
                borderRadius: "0.375rem",
                borderStyle: "solid",
                borderWidth: "1px",
                borderColor: "rgba(255, 255, 255, 0.07)",
                boxShadow: "0 0px 6px 0 rgba(0,0,0,0.25)",
              }
            },
          }
        : {}),
    },
    className: `rounded-md ${selectClassName} whitespace-nowrap`,
    theme: (theme) => ({
      ...theme,
      colors: {
        ...theme.colors,
        primary25: "#2C3137",
        primary50: "#2C3137",
        dangerLight: "#2C3137",
      },
    }),
    components: hideDropdownIndicator ? hiddenDropdownIndicator : {},
    ...props,
  }

  return (
    (<div
      className={classnames(
        overrideWrapperClassname,
        isDisabled ? "text-grayscale-lightest cursor-not-allowed" : null,
        autoWidth ? "whitespace-nowrap" : null,
        "relative"
      )}
    >
      {portalDropdown && (
        <div
          className="absolute top-0 bottom-0 right-0 left-0 pointer-events-none "
          ref={ref}
        ></div>
      )}
      {!creatable ? (
        // @ts-ignore
        (<Select
          {...selectProps}
          closeMenuOnScroll={true}
          // menuShouldBlockScroll={true}
          // menuShouldScrollIntoView={true}
          // menuPortalTarget={ref.current}
          onMenuOpen={() => {
            if (selectProps.onMenuOpen) {
              selectProps.onMenuOpen()
            }
            if (portalDropdown && ref.current) {
              ref.current.scrollIntoView({
                behavior: "auto",
                block: "nearest",
                inline: "center",
              })
            }

            console.log("[!] menu opened", ref.current?.getBoundingClientRect(), window.scrollY)
          }}
          onKeyDown={(e) => {
            // if (e.key === "Enter") {
            //   e.preventDefault()
            // }
          }}
        />)
      ) : (
        // @ts-ignore
        (<CreatableSelect
          {...selectProps}
          closeMenuOnScroll={true}
          // menuShouldBlockScroll={true}
          // menuShouldScrollIntoView={true}
          // menuPortalTarget={ref.current}
          onMenuOpen={() => {
            if (selectProps.onMenuOpen) {
              selectProps.onMenuOpen()
            }
            if (portalDropdown && ref.current) {
              ref.current.scrollIntoView({
                behavior: "auto",
                block: "nearest",
                inline: "center",
              })
            }
            console.log("[!] menu opened", ref.current?.getBoundingClientRect(), window.scrollY)
          }}
          onKeyDown={(e) => {
            // if (e.key === "Enter") {
            //   e.preventDefault()
            // }
          }}
        />)
      )}
    </div>)
  );
}
